import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DefaultResponse } from '@core/models/Http/DefaultResponse';
import { PoliticaPrivacidad } from '@core/models/Registration';
import { environment } from '@enviroment/environment';
import { BehaviorSubject, Observable, map } from 'rxjs';

export interface CookiesConsentCategories {
  tecnicas: boolean;
  analiticas: boolean;
  [key: string]: boolean;
}

export interface CookiesConsentStatus {
  requiereConsentimiento: boolean;
  idCookiesConsentimiento: number | null;
  idPoliticaPrivacidad: number | null;
  versionPolitica: number | null;
  decision: string | null;
  categorias: CookiesConsentCategories;
  fechaDecisionUtc: string | null;
}

export interface SaveCookiesConsentPayload {
  idPoliticaPrivacidad: number;
  versionPolitica: number;
  canal: 'WEB';
  decision: 'ACEPTAR_TODAS' | 'RECHAZAR_NO_NECESARIAS' | 'CONFIGURACION_GUARDADA';
  categorias: CookiesConsentCategories;
  sessionId: string;
  deviceId: string | null;
}

export interface SaveCookiesConsentResponse extends CookiesConsentStatus {
  guardado: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class CookiesConsentService {
  private readonly sessionStorageKey = 'rmd.cookies.sessionId';
  private readonly analyticsAllowedSubject = new BehaviorSubject<boolean>(false);

  readonly analyticsAllowed$ = this.analyticsAllowedSubject.asObservable();

  constructor(private http: HttpClient) {}

  getOrCreateSessionId(): string {
    const existing = this.readSessionId();
    if (existing) {
      return existing;
    }

    const generated = this.generateSessionId();
    this.writeSessionId(generated);
    return generated;
  }

  getCurrentConsent(sessionId: string): Observable<CookiesConsentStatus> {
    const params = new HttpParams()
      .set('canal', 'WEB')
      .set('sessionId', sessionId);

    return this.http
      .get<DefaultResponse<any>>(`${environment.backend_url}Consentimientos/cookies/actual`, { params })
      .pipe(map((response) => this.normalizeStatus(response.data)));
  }

  getCookiePolicy(): Observable<PoliticaPrivacidad | null> {
    const params = new HttpParams().set('tipo', 'COOKIES');

    return this.http
      .get<DefaultResponse<any[]>>(`${environment.backend_url}PoliticasPrivacidad`, { params })
      .pipe(
        map((response) => (response.data ?? []).map((item) => this.normalizePolicy(item))),
        map((items) => items.find((item) => this.isTruthy(item.activo)) ?? null)
      );
  }

  saveConsent(payload: SaveCookiesConsentPayload): Observable<SaveCookiesConsentResponse> {
    return this.http
      .post<DefaultResponse<any>>(`${environment.backend_url}Consentimientos/cookies`, payload)
      .pipe(map((response) => this.normalizeSaveResponse(response.data)));
  }

  setAnalyticsAllowed(allowed: boolean): void {
    this.analyticsAllowedSubject.next(allowed);
  }

  private normalizeStatus(item: any): CookiesConsentStatus {
    return {
      requiereConsentimiento: this.isTruthy(
        item?.requiereConsentimiento ?? item?.RequiereConsentimiento
      ),
      idCookiesConsentimiento: this.toNullableNumber(
        item?.idCookiesConsentimiento ?? item?.IdCookiesConsentimiento
      ),
      idPoliticaPrivacidad: this.toNullableNumber(
        item?.idPoliticaPrivacidad ?? item?.IdPoliticaPrivacidad
      ),
      versionPolitica: this.toNullableNumber(
        item?.versionPolitica ?? item?.VersionPolitica ?? item?.edicion ?? item?.Edicion
      ),
      decision: this.toNullableString(item?.decision ?? item?.Decision),
      categorias: this.normalizeCategories(item?.categorias ?? item?.Categorias),
      fechaDecisionUtc: this.toNullableString(item?.fechaDecisionUtc ?? item?.FechaDecisionUtc),
    };
  }

  private normalizeSaveResponse(item: any): SaveCookiesConsentResponse {
    return {
      guardado: this.isTruthy(item?.guardado ?? item?.Guardado),
      ...this.normalizeStatus(item),
    };
  }

  private normalizePolicy(item: any): PoliticaPrivacidad {
    return {
      idPoliticaPrivacidad: Number(item?.idPoliticaPrivacidad ?? item?.IdPoliticaPrivacidad ?? 0),
      titulo: String(item?.titulo ?? item?.Titulo ?? ''),
      tipo: String(item?.tipo ?? item?.Tipo ?? ''),
      edicion: Number(item?.edicion ?? item?.Edicion ?? 0),
      contenido: String(item?.contenido ?? item?.Contenido ?? ''),
      requiereAceptacion: this.isTruthy(item?.requiereAceptacion ?? item?.RequiereAceptacion),
      activo: this.isTruthy(item?.activo ?? item?.Activo),
      fechaCreacion: this.toNullableString(item?.fechaCreacion ?? item?.FechaCreacion),
    };
  }

  private normalizeCategories(raw: any): CookiesConsentCategories {
    return {
      tecnicas: this.isTruthy(raw?.tecnicas ?? raw?.Tecnicas ?? true),
      analiticas: this.isTruthy(raw?.analiticas ?? raw?.Analiticas),
    };
  }

  private isTruthy(value: unknown): boolean {
    return value === true || value === 1 || value === '1' || value === 'true';
  }

  private toNullableNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const normalized = Number(value);
    return Number.isFinite(normalized) ? normalized : null;
  }

  private toNullableString(value: unknown): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    return String(value);
  }

  private readSessionId(): string | null {
    try {
      return localStorage.getItem(this.sessionStorageKey);
    } catch {
      return null;
    }
  }

  private writeSessionId(value: string): void {
    try {
      localStorage.setItem(this.sessionStorageKey, value);
    } catch {
      // ignore storage failures and keep in-memory usage only
    }
  }

  private generateSessionId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    return `rmd-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
  }
}
